<script setup lang="ts">
/**
 * Manuscript / note / Database entry shell: ProseMirror (`src/editor/prose/`)
 * for `.zwdoc`, plus chrome (ribbon, format bubble, comment margin, image
 * toolbar, find bar, link dialog, history / trash review). Maps (`.zwmap`)
 * mount via `MapEditorHost.vue` on the `database-map` route (#268).
 */
import { trimmedMaybe } from "../lib/trimmedString";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from "vue";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import {
  isChapterOpeningScene,
  sceneFirstParagraph,
} from "../lib/chapterOpening";
import { defaultZwParagraphFormatting } from "../lib/zwConfig";
import CommentMargin from "./CommentMargin.vue";
import EntityFieldsPanel from "./EntityFieldsPanel.vue";
import SceneTitleBar from "./SceneTitleBar.vue";
import SceneNeighborPeek from "./SceneNeighborPeek.vue";
import ChangeHistoryPanel from "./ChangeHistoryPanel.vue";
import { STATUS_BAR_PANEL_CONTENT_ID } from "./EditorStatusBar.vue";
import { useKeyboardInset } from "../composables/useKeyboardInset";
import { useSceneNeighborPeeks } from "../composables/useSceneNeighborPeeks";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import FormatBubble from "./FormatBubble.vue";
import FormatRibbon from "./FormatRibbon.vue";
import ImageAltDialog from "./ImageAltDialog.vue";
import ImageCropDialog from "./ImageCropDialog.vue";
import ImageToolbar from "./ImageToolbar.vue";
import LinkDialog from "./LinkDialog.vue";
import { useUiZoom } from "../composables/useUiZoom";
import type { CommentSurface } from "../editor/commentSurface";
import { warmupHarper } from "../editor/harperEngine";
import { useEntityPeek } from "../composables/useEntityPeek";
import { useImageToolbar } from "../composables/useImageToolbar";
import { useFormatBubble } from "../composables/useFormatBubble";
import {
  useHistoryReview,
  type ProseReviewDoc,
  type TrashPreviewState,
} from "../composables/useHistoryReview";
import { useCommentThreads } from "../composables/useCommentThreads";
import { useEditorLayout } from "../composables/useEditorLayout";
import { pickAndImportImage } from "../editor/imageInsert";
import { resolveImagePreviewSrc } from "../editor/imagePreviewUrls";
import { assertNever } from "../lib/assertNever";
import { assetStorageRoot } from "../lib/projectAssets";
import { useEditorFont } from "../composables/useEditorFont";
import { storeToRefs } from "pinia";
import { useEditorChromeStore } from "../stores/useEditorChromeStore";
import { useEditorSaveStore } from "../stores/useEditorSaveStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useDisplayModeStore } from "../stores/useDisplayModeStore";
import { useShareModeStore } from "../stores/useShareModeStore";
import { useWordLookupStore } from "../stores/useWordLookupStore";
import {
  NO_EDITOR_CHAPTER_OPENING,
  type EditorChapterOpening,
  type EditorPort,
  type EditorPortEvents,
  type FormatCommand,
} from "../editor/port";
import ProseDocEditor from "../editor/prose/ProseDocEditor.vue";
import type {
  ProseSearchCommand,
  ProseSearchQuery,
  ProseShellRequest,
} from "../editor/prose/proseEditor";
import ProseFindBar from "../editor/prose/ProseFindBar.vue";
import { isCloudReaderHosted } from "../lib/cloudEditorHost";
import { getCloudSession } from "../lib/cloudAuth";
import { setCommentAuthor } from "../editor/commentAuthor";
import { displayModeIsReading } from "../lib/displayMode";
import { projectRelativePath } from "../lib/fileHistory";
import { findLastGoodFileVersion } from "../lib/zwdoc/recoverFromHistory";
import { newZwDocText } from "../lib/zwdoc/format";
import cloudIconUrl from "../assets/map-landmarks/cloud.svg";

const session = useEditorSessionStore();
/** Styling ▸ Paragraphs, read by `prose.css` from `.zw-prose-surface` data attrs.
 * The chapter opening only applies to a chapter's first scene. */
const paragraphFormatting = computed(() =>
  matchMaybe(session.projectConfig, {
    just: (config) => config.paragraphFormatting,
    nothing: () => defaultZwParagraphFormatting(),
  }),
);
/** The chapter opening applies only to a chapter's first scene. */
const opensChapter = computed(() =>
  matchMaybe(session.projectConfig, {
    just: (config) =>
      matchMaybe(session.activeBasename, {
        just: (path) => isChapterOpeningScene(config.manuscript, path),
        nothing: () => false,
      }),
    nothing: () => false,
  }),
);
const firstParagraph = computed(() =>
  sceneFirstParagraph(
    paragraphFormatting.value.firstParagraph,
    opensChapter.value,
  ),
);
const chapterOpening = computed((): EditorChapterOpening => {
  if (opensChapter.value === false) return NO_EDITOR_CHAPTER_OPENING;
  const { openingInitial, openingLeadIn } = paragraphFormatting.value;
  return { initial: openingInitial !== "none", leadIn: openingLeadIn };
});
const save = useEditorSaveStore();
const displayStore = useDisplayModeStore();
const share = useShareModeStore();
const wordLookup = useWordLookupStore();
const workspace = useProjectWorkspaceStore();
const { typewriterActive } = storeToRefs(displayStore);
const { displayMode, isFocus, isReading, allowsComments, isWriting } =
  storeToRefs(displayStore);
const {
  sceneFormatting: neighborSceneFormatting,
  prevPeek,
  nextPeek,
} = useSceneNeighborPeeks();

function openNeighborScene(path: string): void {
  void workspace.onSelectProjectFile(path);
}

function syncCommentAuthorFromSession(): void {
  const cloudSession = getCloudSession();
  if (cloudSession !== null && cloudSession.email !== "") {
    setCommentAuthor({
      email: cloudSession.email,
      userId: cloudSession.userId,
    });
  } else {
    setCommentAuthor(null);
  }
}

const { bodyFontId, headingFontId, setBodyFont, setHeadingFont } =
  useEditorFont();

const {
  mobileShell,
  kind: mode,
  rootPath,
  activePath,
  outlineActiveFile: activeFile,
  activeNote,
  activeDatabase,
  modLabel,
  altLabel,
  showRibbonFonts: showFonts,
  outlineOpen,
  commentsOpen,
  historyOpen,
  doc,
  activeCommentId,
  outlineCollapsed,
  contextPanelCollapsed,
  trashPreview,
} = storeToRefs(session);

const emit = defineEmits<EditorPortEvents>();

const { percent: uiZoomPercent } = useUiZoom();
const shell = useTemplateRef<HTMLDivElement>("shell");
/** The actual `overflow-y: auto` element — title bar + editor scroll together
 * as its flex-column children. `.zw-editor-shell` stays non-scrolling so the
 * viewport-relative overlays (comment margin, format bubble, toolbars,
 * dialogs) anchored to it don't scroll away with the content. */
const scrollHost = useTemplateRef<HTMLDivElement>("scrollHost");
const shellForEditor = computed(() => toMaybe(shell.value));
const scrollHostMaybe = computed(() => toMaybe(scrollHost.value));

/** Keep the reading position when the layout flips between the desktop and
 * narrow (mobile) layouts (ZW-276). The editor stays mounted, so selection
 * survives on its own; scroll is kept as a fraction because line wrapping
 * changes the content height. Runs before the DOM update (flush "pre"). */
watch(mobileShell, async () => {
  const host = scrollHost.value;
  if (host === null) return;
  const range = host.scrollHeight - host.clientHeight;
  const ratio = range > 0 ? host.scrollTop / range : 0;
  await nextTick();
  const after = scrollHost.value;
  if (after === null) return;
  after.scrollTop =
    ratio * Math.max(0, after.scrollHeight - after.clientHeight);
});
let caretRaf = 0;

const proseEditor =
  useTemplateRef<InstanceType<typeof ProseDocEditor>>("proseEditor");

/** Comment UI talks to the editor through its comment surface (#251). */
const getCommentSurface = (): Maybe<CommentSurface> =>
  toMaybe(proseEditor.value?.commentSurface() ?? null);

/** Mobile: user-toggled comments layer (desktop always shows when visible).
 * Reading keeps comments on even without the status-bar toggle (unless the
 * display mode forbids them). */
const commentsLayerActive = computed(
  () =>
    allowsComments.value &&
    (mobileShell.value === false || commentsOpen.value || isReading.value),
);
/** Bridge ref: entity peek / format bubble need reviewModeActive before
 * useHistoryReview can be constructed. useHistoryReview writes this
 * synchronously when entering/leaving review. */
const reviewModeActive = ref(false);

const entityPeek = useEntityPeek({ displayMode, reviewModeActive });

const { keyboardInsetPx } = useKeyboardInset();

const { imagePreviewRevision: imageTick } = storeToRefs(session);

/** Bridge: comment-threads needs to trigger layout before useEditorLayout
 * (which reads comment-threads' openIds) can be constructed. */
let scheduleLayoutBridge = (): void => {};

const commentThreads = useCommentThreads({
  getSurface: getCommentSurface,
  doc,
  activeCommentId,
  mobileShell,
  commentsOpen,
  scheduleLayout: () => scheduleLayoutBridge(),
  emitSelectComment: (id) => emit("selectComment", id),
});
const {
  activeId,
  openIds,
  openId,
  hoveredCommentId,
  commentsVisible,
  setActiveId,
  syncOpenThreads,
  openThread,
  closeThread,
  scrollToComment,
  onSelectComment,
  onReply,
  onUpdateMessage,
  onDeleteMessage,
  onToggleResolved,
  onRemove,
  onCommentTextClick,
  scheduleReveal,
} = commentThreads;

const layout = useEditorLayout({
  getSurface: getCommentSurface,
  doc,
  shell: shellForEditor,
  mobileShell,
  uiZoomPercent,
  outlineCollapsed,
  contextPanelCollapsed,
  openIds,
  commentsEnabled: allowsComments,
});
const {
  proseMotionReady,
  contentReady,
  layoutInstant,
  placements,
  syncProseLayout,
  scheduleLayout,
} = layout;
scheduleLayoutBridge = scheduleLayout;

function setOutlineOpen(open: boolean): void {
  outlineOpen.value = open;
}

/**
 * A `.zwdoc` document is open. (Projects are converted on open, #259;
 * anything else shows the editor's "couldn't be opened" notice.) During
 * history review / trash preview the editor stays mounted but hidden and
 * read-only; the review is a second, read-only `ProseDocEditor`
 * (`proseReviewDoc`, #252).
 */
const documentOpen = computed(() => doc.value.trim() !== "");
const proseReviewDoc = shallowRef<Maybe<ProseReviewDoc>>(nothing);
const proseReview =
  useTemplateRef<InstanceType<typeof ProseDocEditor>>("proseReview");

function showProseReview(review: Maybe<ProseReviewDoc>): void {
  proseReviewDoc.value = review;
  if (isNothing(review)) return;
  const first = toMaybe(review.value.ranges[0]);
  void nextTick(() => {
    // Land on the first change.
    if (isJust(first)) proseReview.value?.scrollToTextOffset(first.value.from);
    else scrollHost.value?.scrollTo({ top: 0 });
  });
}

const proseEditable = computed(
  () => isReading.value === false && reviewModeActive.value === false,
);

const assetRoot = computed(() =>
  assetStorageRoot(mode.value, rootPath.value, activePath.value),
);

const resolveProseImageSrc = computed(() => {
  const root = assetRoot.value;
  const tick = imageTick.value;
  return (src: string): string => resolveImagePreviewSrc(src, root, tick);
});

/** Image edits rewrite assets in place; bump the tick so URLs re-resolve. */
function refreshImages(): void {
  imageTick.value += 1;
}

function toggleComments(): void {
  if (mobileShell.value === false) return;
  commentsOpen.value = commentsOpen.value === false;
  if (commentsOpen.value && historyOpen.value) {
    historyReview.closeHistory();
  }
  scheduleLayout();
}

// --- Find --------------------------------------------------------------------

const proseFind = ref({ open: false, seed: "" });
const proseFindBar =
  useTemplateRef<InstanceType<typeof ProseFindBar>>("proseFindBar");

function openFind(): void {
  const seed = proseEditor.value?.selectedText() ?? "";
  if (proseFind.value.open) proseFindBar.value?.open(seed);
  else proseFind.value = { open: true, seed };
}

function findNext(): void {
  proseEditor.value?.searchCommand("next");
}

function selectNextOccurrence(): void {
  proseEditor.value?.selectNextOccurrence();
}

function onProseFindQuery(query: ProseSearchQuery): void {
  proseEditor.value?.setSearch(query);
}

function onProseFindRun(command: ProseSearchCommand): void {
  proseEditor.value?.searchCommand(command);
}

function closeProseFind(): void {
  proseFind.value = { open: false, seed: "" };
  proseEditor.value?.setSearch({
    search: "",
    replace: "",
    caseSensitive: false,
    wholeWord: false,
  });
  proseEditor.value?.focus();
}

/** Edit → Thesaurus… / CmdOrCtrl+Shift+Y. Writing mode only. */
function openThesaurus(): void {
  if (isWriting.value === false) return;

  const seed = proseEditor.value?.wordOrSelection() ?? nothing;
  wordLookup.openFromEditor(seed);
  session.thesaurusDialogOpen = true;
}

function ensureCaretVisible(behavior: ScrollBehavior = "auto"): void {
  proseEditor.value?.ensureCaretVisible(behavior);
}

function editorHasFocus(): boolean {
  return matchMaybe(proseEditor.value?.selectionSnapshot() ?? nothing, {
    just: (selection) => selection.hasFocus,
    nothing: () => false,
  });
}

/**
 * A viewport change (soft keyboard, window drag) reflows the scroller but leaves
 * scrollTop alone, which can strand the caret below the fold. It also moves the
 * selection, and the bubble's anchor is a snapshot rect — so re-measure both.
 * Coalesced to one frame.
 */
function onViewportResize(): void {
  if (caretRaf !== 0) cancelAnimationFrame(caretRaf);
  caretRaf = requestAnimationFrame(() => {
    caretRaf = 0;
    if (editorHasFocus() === false) return;
    ensureCaretVisible();
    if (bubbleVisible.value) syncFormatBubble();
  });
}

// Opening the keyboard changes only the visual viewport on Android, which fires
// no window `resize` — so react to the measured inset as well.
watch(keyboardInsetPx, onViewportResize);

// --- Link / image requests -----------------------------------------------------

const proseLink = ref({ open: false, href: "", canRemove: false });

async function onProseRequest(command: ProseShellRequest): Promise<void> {
  const editor = proseEditor.value;
  if (editor === null) return;
  switch (command) {
    case "link": {
      const target = editor.linkTarget();
      proseLink.value = {
        open: true,
        href: target.href,
        canRemove: target.editing,
      };
      return;
    }
    case "find":
      openFind();
      return;
    case "image": {
      const src = await pickAndImportImage({
        mode: mode.value,
        rootPath: rootPath.value,
        activePath: activePath.value,
      });
      if (isJust(src)) proseEditor.value?.insertImage(src.value);
      return;
    }
    default:
      assertNever(command);
  }
}

function closeProseLink(): void {
  proseLink.value = { ...proseLink.value, open: false };
  proseEditor.value?.focus();
}

function onProseLinkApply(href: string): void {
  proseLink.value = { ...proseLink.value, open: false };
  proseEditor.value?.applyLink(href);
}

function onProseLinkRemove(): void {
  proseLink.value = { ...proseLink.value, open: false };
  proseEditor.value?.removeLink();
}

// --- Comments ----------------------------------------------------------------

/** Comment id of the highlighted text under an event target, or "". */
function commentIdAt(target: unknown): {
  id: string;
  el: Maybe<HTMLElement>;
} {
  if (target instanceof Element === false) return { id: "", el: nothing };
  const marked = target.closest(".zw-comment-text");
  if (marked instanceof HTMLElement === false) return { id: "", el: nothing };
  return { id: marked.dataset.commentId ?? "", el: just(marked) };
}

/** Click on highlighted comment text: open its thread. */
function onCommentTextPointer(event: MouseEvent): void {
  const { id } = commentIdAt(event.target);
  if (id !== "") onCommentTextClick(id);
}

function onCommentTextOver(event: MouseEvent): void {
  const { id } = commentIdAt(event.target);
  if (id === "") return;
  hoveredCommentId.value = just(id);
}

function onCommentTextOut(event: MouseEvent): void {
  const { id, el } = commentIdAt(event.target);
  if (id === "" || isNothing(el)) return;
  const related = event.relatedTarget;
  if (related instanceof Node && el.value.contains(related)) return;
  if (commentIdAt(related).id === id) return;
  if (isJust(hoveredCommentId.value) && hoveredCommentId.value.value === id) {
    hoveredCommentId.value = nothing;
  }
}

/** The editor added (or reopened) a comment: open its thread card. */
function onProseCommentAdded(id: string): void {
  syncOpenThreads(doc.value);
  openThread(id);
  setActiveId(just(id));
  commentsVisible.value = true;
  emit("commentAdded", id);
}

function onProseText(text: string): void {
  if (reviewModeActive.value) return;
  wordLookup.onEditorDocChanged();
  doc.value = text;
  syncOpenThreads(text);
  scheduleLayout();
}

/**
 * Unreadable buffer (e.g. map JSON written into a `.zwdoc`): restore the newest
 * readable version from git history, or a blank entry when history has none.
 */
async function onProseResetDocument(): Promise<void> {
  if (reviewModeActive.value) return;
  const root = fromMaybe(rootPath.value);
  const abs = fromMaybe(activePath.value);
  let restored = newZwDocText();
  if (root !== null && abs !== null) {
    const relM = projectRelativePath(root, abs);
    if (isJust(relM)) {
      const found = await findLastGoodFileVersion(root, relM.value);
      if (isJust(found)) restored = found.value;
    }
  }
  await save.onRestoreVersion(restored);
  // If persist was refused (no path), still put readable text on screen.
  if (doc.value !== restored) doc.value = restored;
  syncOpenThreads(doc.value);
  scheduleLayout();
  await nextTick();
  proseEditor.value?.focus();
}

function focus(): void {
  proseEditor.value?.focus();
}

/** Ribbon / menu commands. */
function runEditorCommand(command: FormatCommand): void {
  if (reviewModeActive.value) return;
  proseEditor.value?.runCommand(command);
}

const historyActiveFile = computed(() => activeFile.value);
const historyActiveNote = computed(() => activeNote.value);
const historyActiveDatabase = computed(() => activeDatabase.value);

/** Set once useFormatBubble exists below — image-toolbar and format-bubble
 * each need a way to reach into the other (open toolbar hides the bubble;
 * the bubble hides itself while an image dialog is open), so this is the
 * one wire that closes the loop between the two composables. */
let hideFormatBubble = (): void => {};

const {
  activeImage,
  imageToolbarVisible,
  imageToolbarAnchor,
  imageToolbarBusy,
  cropDialogOpen,
  cropDialogDataUrl,
  altDialogOpen,
  closeImageToolbar,
  openImageToolbar,
  onImageToolbarDelete,
  onImageToolbarRotate,
  onImageToolbarCrop,
  onImageToolbarDescribe,
  onAltDialogApply,
  onAltDialogClose,
  onImageCropApply,
  onCropDialogClose,
} = useImageToolbar({
  imageOps: () => {
    const prose = proseEditor.value;
    if (prose === null) return nothing;
    return just({
      remove: (image) => prose.removeImage(image),
      updateAlt: (image, alt) => toMaybe(prose.updateImageAlt(image, alt)),
    });
  },
  shell: shellForEditor,
  assetRoot,
  refreshImages,
  hideFormatBubble: () => hideFormatBubble(),
});

const activeImageAlt = computed(() =>
  matchMaybe(activeImage.value, {
    just: (image) => image.alt,
    nothing: () => "",
  }),
);

const {
  bubbleAnchor,
  bubbleVisible,
  syncFormatBubble,
  onBubblePin,
  onBubbleDismiss,
} = useFormatBubble({
  getSelection: () => proseEditor.value?.selectionSnapshot() ?? nothing,
  collapseSelection: () => {
    console.log("BUBBLEKEY collapse", proseEditor.value === null);
    proseEditor.value?.collapseSelection();
  },
  shell: shellForEditor,
  mobileShell,
  displayMode,
  reviewModeActive,
  keyboardInsetPx,
  openId,
  imageToolbarVisible,
  altDialogOpen,
  cropDialogOpen,
});
hideFormatBubble = () => {
  bubbleVisible.value = false;
};

function onBubbleCommand(command: FormatCommand): void {
  runEditorCommand(command);
  syncFormatBubble();
  emit("formatCommand", command);
}

function onProseSelectionChange(): void {
  syncFormatBubble();
  scheduleLayout();
}

const historyReview = useHistoryReview({
  reviewModeActive,
  doc,
  mode,
  rootPath,
  activePath,
  activeFile: historyActiveFile,
  activeNote: historyActiveNote,
  activeDatabase: historyActiveDatabase,
  historyOpen,
  mobileShell,
  commentsOpen,
  bubbleVisible,
  proseMotionReady,
  layoutInstant,
  scheduleLayout,
  onTrashPreviewChange: (preview: TrashPreviewState) => {
    session.trashPreview = preview;
  },
  onRestoreVersion: (content: string) => save.onRestoreVersion(content),
  showReview: showProseReview,
});

const {
  trashPreviewActive,
  historyRelativePath,
  historyFileLabel,
  canShowHistory,
  showTrashPreview,
  clearTrashPreview,
  onHistorySelect,
  onHistoryRestore,
  toggleHistory,
  closeHistory,
} = historyReview;

useEditorChromeStore().register({
  toggleHistory,
  toggleComments,
  closeHistory,
  openFind,
  findNext,
  selectNextOccurrence,
  // Thesaurus stays on EditorShell (dialog + writing-mode gate).
});

wordLookup.registerEditor(
  just({
    replaceExpectedRange: (range, text) =>
      proseEditor.value?.replaceExpectedRange(range, text) ?? false,
  }),
);

/** `from` is a plain-text projection offset (occurrences, project search). */
function scrollToHeading(from: number): void {
  requestAnimationFrame(() => proseEditor.value?.scrollToTextOffset(from));
}

defineProps<{
  demo?: boolean;
}>();

defineExpose({
  focus,
  runCommand: runEditorCommand,
  scrollToComment,
  scrollToHeading,
  toggleHistory,
  toggleComments,
  closeHistory,
  openFind,
  findNext,
  selectNextOccurrence,
  openThesaurus,
  ensureCaretVisible,
  toggleOutline: () => setOutlineOpen(outlineOpen.value === false),
  showTrashPreview,
  clearTrashPreview,
} satisfies EditorPort);

function onKeydown(event: KeyboardEvent): void {
  const key = event.key.toLowerCase();
  const mod = event.metaKey || event.ctrlKey;

  if (mod && event.shiftKey && key === "a") {
    event.preventDefault();
    outlineCollapsed.value = outlineCollapsed.value === false;
    return;
  }
}

onMounted(() => {
  window.addEventListener("keydown", onKeydown);

  // Session may already hold a trash preview (set before this mount).
  if (isJust(session.trashPreview)) {
    const pendingTrash = session.trashPreview.value;
    showTrashPreview(
      pendingTrash.content,
      pendingTrash.label,
      pendingTrash.path,
    );
  }

  // Share readers are read-only and cannot load Database entries for the
  // lexicon. Skip warmup so Harper never boots on /reader.
  if (share.shareMode === false && isCloudReaderHosted() === false) {
    warmupHarper();
  }

  syncCommentAuthorFromSession();
  scrollHost.value?.addEventListener("scroll", scheduleLayout, {
    passive: true,
  });
  scrollHost.value?.addEventListener("scroll", syncFormatBubble, {
    passive: true,
  });
  window.addEventListener("resize", scheduleLayout);
  window.addEventListener("resize", onViewportResize);
  syncOpenThreads(doc.value);
  const activeTrimmed = trimmedMaybe(fromMaybe(activeId.value));
  if (isJust(activeTrimmed)) {
    openThread(activeTrimmed.value);
  }
  // First layout(s) run before the column is shown, so it appears already
  // centered. Margin changes after that are instant.
  syncProseLayout();
  scheduleLayout();
  void nextTick(() => {
    syncProseLayout();
    scheduleLayout();
    requestAnimationFrame(() => {
      syncProseLayout();
      contentReady.value = true;
      requestAnimationFrame(() => {
        proseMotionReady.value = true;
        scheduleReveal(320);
      });
    });
  });
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
});

watch(
  () => doc.value,
  (value) => {
    if (reviewModeActive.value) return;
    syncOpenThreads(value);
    scheduleLayout();
  },
);

watch(displayMode, (mode, prev) => {
  if (mode === "focus" || mode === "reading") {
    bubbleVisible.value = false;
    if (mode === "focus") closeImageToolbar();
  }
  if (
    typeof prev !== "undefined" &&
    displayModeIsReading(mode) !== displayModeIsReading(prev)
  ) {
    closeImageToolbar();
    syncFormatBubble();
  }
});

watch(openId, () => syncFormatBubble());

onBeforeUnmount(() => {
  commentThreads.cleanup();
  entityPeek.cleanup();
  layout.cleanup();
  if (caretRaf !== 0) cancelAnimationFrame(caretRaf);
  window.removeEventListener("resize", scheduleLayout);
  window.removeEventListener("resize", onViewportResize);
  setCommentAuthor(null);
  scrollHost.value?.removeEventListener("scroll", scheduleLayout);
  scrollHost.value?.removeEventListener("scroll", syncFormatBubble);
  wordLookup.registerEditor(nothing);
});
</script>

<template>
  <div class="relative flex h-full min-h-0 overflow-hidden">
    <div
      ref="shell"
      class="zw-editor-shell relative flex flex-col min-h-0 min-w-0 flex-1 overflow-hidden"
      :class="{
        'zw-prose-motion': proseMotionReady && isWriting,
        'zw-content-loading': !contentReady,
      }"
    >
      <header
        v-if="mobileShell === false && (isJust(activePath) || demo)"
        v-show="isWriting"
        class="zw-format-header shrink-0 border-b"
        style="
          border-color: var(--line);
          background: color-mix(in oklch, var(--bg) 88%, transparent);
          backdrop-filter: blur(8px);
        "
      >
        <FormatRibbon
          :mod-label="modLabel"
          :alt-label="altLabel"
          :body-font-id="bodyFontId"
          :heading-font-id="headingFontId"
          :show-fonts="showFonts"
          @command="runEditorCommand"
          @update:body-font-id="setBodyFont($event, true)"
          @update:heading-font-id="setHeadingFont($event, true)"
        />
      </header>
      <div
        v-show="
          isJust(activePath) ||
          demo ||
          trashPreviewActive ||
          isJust(trashPreview)
        "
        ref="scrollHost"
        class="zw-editor-scroll flex-1 min-h-0 flex flex-col overflow-y-auto"
      >
        <SceneTitleBar />
        <EntityFieldsPanel />
        <SceneNeighborPeek
          v-if="reviewModeActive === false"
          edge="prev"
          :peek="prevPeek"
          :scene-formatting="neighborSceneFormatting"
          :paragraph-style="paragraphFormatting.style"
          :resolve-image-src="resolveProseImageSrc"
          :scroll-host="scrollHostMaybe"
          :entity-index="entityPeek.index.value"
          :mobile="mobileShell"
          @open="openNeighborScene"
        />
        <div
          class="zw-prose-surface"
          :data-paragraph-style="paragraphFormatting.style"
          :data-first-paragraph="firstParagraph"
          :data-opening-initial="
            opensChapter === true ? paragraphFormatting.openingInitial : 'none'
          "
          :data-opening-lead-in="
            opensChapter === true ? paragraphFormatting.openingLeadIn : 'none'
          "
          :data-peek-above="isJust(prevPeek) ? 'true' : 'false'"
          :data-peek-below="isJust(nextPeek) ? 'true' : 'false'"
        >
          <ProseDocEditor
            v-if="documentOpen"
            v-show="reviewModeActive === false"
            ref="proseEditor"
            :text="doc"
            :editable="proseEditable"
            :resolve-image-src="resolveProseImageSrc"
            :scroll-host="scrollHost"
            :typewriter="typewriterActive"
            :lint="isWriting && reviewModeActive === false"
            :entity-index="entityPeek.index.value"
            :peek="entityPeek.peekContext"
            :comments="allowsComments && reviewModeActive === false"
            :history-ranges="[]"
            :chapter-opening="chapterOpening"
            :mobile="mobileShell"
            @update:text="onProseText"
            @reset-document="onProseResetDocument"
            @selection-change="onProseSelectionChange"
            @comment-added="onProseCommentAdded"
            @click="onCommentTextPointer"
            @mouseover="onCommentTextOver"
            @mouseout="onCommentTextOut"
            @request="onProseRequest"
            @image-click="openImageToolbar"
          />
        </div>
        <SceneNeighborPeek
          v-if="reviewModeActive === false"
          edge="next"
          :peek="nextPeek"
          :scene-formatting="neighborSceneFormatting"
          :paragraph-style="paragraphFormatting.style"
          :resolve-image-src="resolveProseImageSrc"
          :scroll-host="scrollHostMaybe"
          :entity-index="entityPeek.index.value"
          :mobile="mobileShell"
          @open="openNeighborScene"
        />
        <div
          class="zw-prose-surface"
          :data-paragraph-style="paragraphFormatting.style"
          :data-first-paragraph="firstParagraph"
          :data-opening-initial="
            opensChapter === true ? paragraphFormatting.openingInitial : 'none'
          "
          :data-opening-lead-in="
            opensChapter === true ? paragraphFormatting.openingLeadIn : 'none'
          "
        >
          <ProseDocEditor
            v-if="isJust(proseReviewDoc)"
            ref="proseReview"
            class="zw-prose-review"
            :text="proseReviewDoc.value.text"
            :editable="false"
            :resolve-image-src="resolveProseImageSrc"
            :scroll-host="scrollHost"
            :typewriter="false"
            :lint="false"
            :entity-index="[]"
            :peek="entityPeek.peekContext"
            :comments="false"
            :history-ranges="proseReviewDoc.value.ranges"
            :chapter-opening="NO_EDITOR_CHAPTER_OPENING"
            :mobile="mobileShell"
          />
        </div>
      </div>
      <div
        v-if="
          isNothing(activePath) &&
          !trashPreviewActive &&
          isNothing(trashPreview)
        "
        class="zw-editor-empty"
      >
        <span
          class="zw-editor-empty-icon"
          role="img"
          aria-label="Empty state"
          :style="{ '--zw-empty-icon': `url(${cloudIconUrl})` }"
        />
      </div>
      <FormatBubble
        :visible="
          displayMode !== 'focus' &&
          displayMode !== 'reading' &&
          bubbleVisible &&
          !reviewModeActive
        "
        :anchor="bubbleAnchor"
        :mod-label="modLabel"
        :alt-label="altLabel"
        :show-fonts="showFonts && isWriting"
        :body-font-id="bodyFontId"
        :heading-font-id="headingFontId"
        :touch-mode="mobileShell"
        :comment-only="displayMode === 'readingComments'"
        @command="onBubbleCommand"
        @pin="onBubblePin"
        @dismiss="onBubbleDismiss"
        @update:body-font-id="setBodyFont($event, true)"
        @update:heading-font-id="setHeadingFont($event, true)"
      />
      <ImageToolbar
        :open="isWriting && imageToolbarVisible && !reviewModeActive"
        :anchor="imageToolbarAnchor"
        :image="activeImage"
        :busy="imageToolbarBusy"
        @close="closeImageToolbar"
        @describe="onImageToolbarDescribe"
        @rotate="onImageToolbarRotate"
        @crop="onImageToolbarCrop"
        @delete="onImageToolbarDelete"
      />
      <ProseFindBar
        v-if="documentOpen && proseFind.open"
        ref="proseFindBar"
        :seed="proseFind.seed"
        @query="onProseFindQuery"
        @run="onProseFindRun"
        @close="closeProseFind"
      />
      <LinkDialog
        :open="proseLink.open"
        :href="proseLink.href"
        :can-remove="proseLink.canRemove"
        @close="closeProseLink"
        @apply="onProseLinkApply"
        @remove="onProseLinkRemove"
      />
      <ImageAltDialog
        :open="altDialogOpen"
        :alt="activeImageAlt"
        @close="onAltDialogClose"
        @apply="onAltDialogApply"
      />
      <ImageCropDialog
        :open="cropDialogOpen"
        :data-url="cropDialogDataUrl"
        @close="onCropDialogClose"
        @apply="onImageCropApply"
      />
      <CommentMargin
        v-show="
          !isFocus && !historyOpen && !trashPreviewActive && commentsLayerActive
        "
        class="zw-comment-layer"
        :class="{
          'zw-comment-layer-visible': commentsVisible,
          'zw-comment-layer-instant': layoutInstant,
        }"
        :doc="doc"
        :active-id="activeId"
        :open-id="openId"
        :hovered-id="hoveredCommentId"
        :placements="placements"
        @select="onSelectComment"
        @collapse="closeThread"
        @reply="onReply"
        @update-message="onUpdateMessage"
        @delete-message="onDeleteMessage"
        @toggle-resolved="onToggleResolved"
        @remove="onRemove"
        @layout="scheduleLayout"
      />
    </div>
    <Teleport defer :to="`#${STATUS_BAR_PANEL_CONTENT_ID}`">
      <ChangeHistoryPanel
        v-if="canShowHistory && historyOpen"
        :open="historyOpen"
        :root-path="rootPath"
        :relative-path="historyRelativePath"
        :file-label="historyFileLabel"
        @select="onHistorySelect"
        @restore="onHistoryRestore"
      />
    </Teleport>
  </div>
</template>

<style scoped>
.zw-editor-empty {
  flex: 1;
  min-height: 0;
  display: grid;
  place-items: center;
  padding: 2rem;
  text-align: center;
  font-family: var(--font-ui);
  font-size: 0.95rem;
  color: var(--text-muted);
}

.zw-editor-empty-icon {
  display: block;
  width: 4rem;
  height: 4rem;
  background-color: var(--text-muted);
  -webkit-mask: var(--zw-empty-icon) center / contain no-repeat;
  mask: var(--zw-empty-icon) center / contain no-repeat;
  opacity: 0.2;
}

/* Extra bottom space so the last lines (and their margin comments) can
   scroll up to mid-viewport. Grows with the history dock and status dock
   (via --zw-editor-bottom-inset / --zw-editor-dock-inset, the latter set by
   EditorApp.vue) so short docs can scroll out from under either panel.
   Lives here (not on the editor) since this wrapper owns scroll. Uses custom properties rather than a plain padding-bottom so
   EditorApp.vue's dock clearance composes instead of overriding this. */
.zw-editor-scroll {
  padding-bottom: max(
    50vh,
    calc(
      var(--zw-editor-bottom-inset, 0px) + var(--zw-editor-dock-inset, 0px) +
        1.5rem
    )
  );
}

/* Keep the last lines reachable above the find/replace bar (mounted on shell). */
.zw-editor-shell:has(.zw-find-bar) .zw-editor-scroll {
  padding-bottom: max(
    50vh,
    calc(
      var(--zw-editor-bottom-inset, 0px) + var(--zw-editor-dock-inset, 0px) +
        1.5rem + 2.75rem
    )
  );
}

/* Neighbor peeks sit flush against the body. Match the usual 1.5rem bottom
   pad so the seam above and below the open scene is the same width. */
.zw-prose-surface[data-peek-above="true"] :deep(.zw-prose) {
  padding-top: 1.5rem;
}

.zw-prose-surface[data-peek-below="true"] :deep(.zw-prose) {
  padding-bottom: 1.5rem;
}

/* Mask the flush-left flash before the first layout pass centers the prose
   column. Instant hide/show (no opacity transition) so content appears
   already centered. Opacity (not display/visibility) so the editor still
   measures while hidden. */
.zw-content-loading {
  opacity: 0;
}

.zw-comment-layer {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.55s var(--ease-out-quart);
}

.zw-comment-layer-visible {
  opacity: 1;
  visibility: visible;
}

.zw-comment-layer-instant :deep([data-margin-comment]) {
  transition: none !important;
}
</style>
